import { useCallback, useEffect, useState } from 'react';
import {
  PhieuYeuCauKiemKe,
  PhieuYeuCauKiemKeInput,
  PhieuYeuCauKiemKeQueryParams,
  PhieuYeuCauKiemKeResponse
} from '@/types/schemas/phieu-yeu-cau-kiem-ke.type';
import { useAuth } from '@/contexts/auth-context';
import QUERY_KEYS from '@/constants/query-keys';
import api from '@/lib/api';

interface UsePhieuYeuCauKiemKeReturn {
  phieuYeuCauKiemKes: PhieuYeuCauKiemKe[];
  isLoading: boolean;
  addPhieuYeuCauKiemKe: (newPhieuYeuCauKiemKe: PhieuYeuCauKiemKeInput) => Promise<PhieuYeuCauKiemKe>;
  updatePhieuYeuCauKiemKe: (
    uuid: string,
    updatedPhieuYeuCauKiemKe: PhieuYeuCauKiemKeInput
  ) => Promise<PhieuYeuCauKiemKe>;
  deletePhieuYeuCauKiemKe: (uuid: string) => Promise<void>;
  refreshPhieuYeuCauKiemKes: () => Promise<void>;
  getPhieuYeuCauKiemKeById: (uuid: string) => Promise<PhieuYeuCauKiemKe | null>;
  getPhieuYeuCauKiemKeByDocumentNumber: (documentNumber: string) => Promise<PhieuYeuCauKiemKe | null>;
  getPhieuYeuCauKiemKesByWarehouse: (warehouseCode: string) => Promise<PhieuYeuCauKiemKe[]>;
  getPhieuYeuCauKiemKesByStatus: (status: string) => Promise<PhieuYeuCauKiemKe[]>;
  getPhieuYeuCauKiemKesByDateRange: (startDate: string, endDate: string) => Promise<PhieuYeuCauKiemKe[]>;
}

/**
 * Hook for managing PhieuYeuCauKiemKe (Inventory Check Request) data
 *
 * This hook provides functions to fetch, create, update, and delete inventory check requests.
 */
export const usePhieuYeuCauKiemKe = (
  initialPhieuYeuCauKiemKes: PhieuYeuCauKiemKe[] = []
): UsePhieuYeuCauKiemKeReturn => {
  const [phieuYeuCauKiemKes, setPhieuYeuCauKiemKes] = useState<PhieuYeuCauKiemKe[]>(initialPhieuYeuCauKiemKes);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  /**
   * Fetch all inventory check requests
   */
  const fetchPhieuYeuCauKiemKes = useCallback(async (): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<PhieuYeuCauKiemKeResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_KIEM_KE}/`
      );
      setPhieuYeuCauKiemKes(response.data.results);
    } catch (error) {
      console.error('Error fetching inventory check requests:', error);
    } finally {
      setIsLoading(false);
    }
  }, [entity?.slug]);

  // Load inventory check requests on component mount
  useEffect(() => {
    fetchPhieuYeuCauKiemKes();
  }, [entity?.slug, fetchPhieuYeuCauKiemKes]);

  /**
   * Get inventory check request by ID
   */
  const getPhieuYeuCauKiemKeById = async (uuid: string): Promise<PhieuYeuCauKiemKe | null> => {
    if (!entity?.slug) return null;

    try {
      const found = phieuYeuCauKiemKes.find(item => item.uuid === uuid);
      if (found) return found;

      // If not found in local state, fetch from API
      const response = await api.get<PhieuYeuCauKiemKe>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_KIEM_KE}/${uuid}/`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching inventory check request by ID:', error);
      return null;
    }
  };

  /**
   * Get inventory check request by document number
   */
  const getPhieuYeuCauKiemKeByDocumentNumber = async (documentNumber: string): Promise<PhieuYeuCauKiemKe | null> => {
    if (!entity?.slug) return null;

    try {
      const found = phieuYeuCauKiemKes.find(item => item.so_ct === documentNumber);
      if (found) return found;

      // If not found in local state, fetch from API
      const response = await api.get<PhieuYeuCauKiemKeResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_KIEM_KE}/?so_ct=${documentNumber}`
      );
      return response.data.results.length > 0 ? response.data.results[0] : null;
    } catch (error) {
      console.error('Error fetching inventory check request by document number:', error);
      return null;
    }
  };

  /**
   * Get inventory check requests by warehouse
   */
  const getPhieuYeuCauKiemKesByWarehouse = async (warehouseCode: string): Promise<PhieuYeuCauKiemKe[]> => {
    if (!entity?.slug) return [];

    try {
      const response = await api.get<PhieuYeuCauKiemKeResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_KIEM_KE}/?ma_kho=${warehouseCode}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching inventory check requests by warehouse:', error);
      return [];
    }
  };

  /**
   * Get inventory check requests by status
   */
  const getPhieuYeuCauKiemKesByStatus = async (status: string): Promise<PhieuYeuCauKiemKe[]> => {
    if (!entity?.slug) return [];

    try {
      const response = await api.get<PhieuYeuCauKiemKeResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_KIEM_KE}/?status=${status}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching inventory check requests by status:', error);
      return [];
    }
  };

  /**
   * Get inventory check requests by date range
   */
  const getPhieuYeuCauKiemKesByDateRange = async (startDate: string, endDate: string): Promise<PhieuYeuCauKiemKe[]> => {
    if (!entity?.slug) return [];

    try {
      const response = await api.get<PhieuYeuCauKiemKeResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_KIEM_KE}/?ngay_ct_gte=${startDate}&ngay_ct_lte=${endDate}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching inventory check requests by date range:', error);
      return [];
    }
  };

  /**
   * Add a new inventory check request
   */
  const addPhieuYeuCauKiemKe = async (newPhieuYeuCauKiemKe: PhieuYeuCauKiemKeInput): Promise<PhieuYeuCauKiemKe> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const response = await api.post<PhieuYeuCauKiemKe>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_KIEM_KE}/`,
        newPhieuYeuCauKiemKe
      );
      await fetchPhieuYeuCauKiemKes();
      return response.data;
    } catch (error) {
      console.error('Error adding inventory check request:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Update an existing inventory check request
   */
  const updatePhieuYeuCauKiemKe = async (
    uuid: string,
    updatedPhieuYeuCauKiemKe: PhieuYeuCauKiemKeInput
  ): Promise<PhieuYeuCauKiemKe> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const response = await api.patch<PhieuYeuCauKiemKe>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_KIEM_KE}/${uuid}/`,
        updatedPhieuYeuCauKiemKe
      );
      await fetchPhieuYeuCauKiemKes();
      return response.data;
    } catch (error) {
      console.error('Error updating inventory check request:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete an inventory check request
   */
  const deletePhieuYeuCauKiemKe = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_KIEM_KE}/${uuid}/`);
      await fetchPhieuYeuCauKiemKes();
    } catch (error) {
      console.error('Error deleting inventory check request:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    phieuYeuCauKiemKes,
    isLoading,
    addPhieuYeuCauKiemKe,
    updatePhieuYeuCauKiemKe,
    deletePhieuYeuCauKiemKe,
    refreshPhieuYeuCauKiemKes: fetchPhieuYeuCauKiemKes,
    getPhieuYeuCauKiemKeById,
    getPhieuYeuCauKiemKeByDocumentNumber,
    getPhieuYeuCauKiemKesByWarehouse,
    getPhieuYeuCauKiemKesByStatus,
    getPhieuYeuCauKiemKesByDateRange
  };
};

/**
 * Standalone function to fetch all PhieuYeuCauKiemKe data with pagination
 * Used for Excel export
 */
export const fetchAllPhieuYeuCauKiemKe = async (
  entitySlug: string,
  queryParams: PhieuYeuCauKiemKeQueryParams = {},
  pageSize = 200
): Promise<PhieuYeuCauKiemKe[]> => {
  const allRows: PhieuYeuCauKiemKe[] = [];
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const params = new URLSearchParams({
      ...queryParams,
      page: String(page),
      page_size: String(pageSize)
    });

    const response = await api.get<{ results: PhieuYeuCauKiemKe[]; next: string | null }>(
      `/entities/${entitySlug}/erp/${QUERY_KEYS.PHIEU_YEU_CAU_KIEM_KE}/?${params.toString()}`
    );

    allRows.push(...response.data.results);
    hasMore = Boolean(response.data.next);
    page++;
  }

  return allRows;
};
